import { isAnyIdAvailable } from '@shared/helpers/idGraph/isAnyIdAvailable';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { getAdtechpassValueProxy } from '@shared/helpers/idGraph/getAdtechpassValueProxy';
import { triggerUtiqSecureSignalProvider } from '@idConnect/triggerUtiqSecureSignalProvider';
import { callEventListeners } from '@shared/events/callEventListeners';
import { IdGraphType } from '@idConnect/types';

export const events: Maker<Ctx, Snap, null> = ({
  makeEffectGroup,
  makeEffect,
}) => [
  ...(
    [
      'onConsentChanging',
      'onConsentManagerStatusChanged',
      'onConsentUpdateFinished',
      'onEligibilityChecked',
      'onFlowCompleted',
      'onIdsAvailable',
      'onInitialised',
    ] as const
  ).map((eventName) =>
    makeEffectGroup(
      eventName,
      (_, { pass }) => pass(null),

      ({ makeEffect }) => [
        makeEffect(
          'add',

          (_, { ctx, pass, skip }) => {
            for (const source of [
              ctx.window.Utiq?.config?.listeners?.[eventName],
              (ctx.window.Utiq as any)?.listeners?.[eventName],
            ]) {
              if (Array.isArray(source)) {
                for (const externalListener of source) {
                  if (
                    !ctx.window.Utiq?.listeners?.[eventName]?.includes(
                      externalListener,
                    )
                  ) {
                    return pass(externalListener, 'missing from array style');
                  }
                }
              } else if (typeof source === 'function') {
                if (
                  !ctx.window.Utiq?.listeners?.[eventName]?.includes(source)
                ) {
                  return pass(source, 'missing from direct style');
                }
              }
            }
            return skip();
          },

          (listener, { ctx }) => {
            ctx.window.Utiq ||= {};
            ctx.window.Utiq.listeners ||= {};
            ctx.window.Utiq.listeners[eventName] ||= [];
            ctx.window.Utiq.listeners[eventName].push(listener);

            if (ctx.externalListenersRebroadcastEvent[eventName])
              listener(ctx.externalListenersRebroadcastEvent[eventName]);
          },
        ),
      ],
    ),
  ),

  makeEffect(
    'onIdsAvailable',

    (_, { ctx, snap, name, pass, skip }) => {
      const stored = ctx.window.localStorage.getItem('utiqPass');

      if (!stored) return skip('none found');
      if (stored === ctx.journal[name]) return skip('unchanged');

      if (!snap.localStorage.utiqPass?.connectId?.idGraph?.[0])
        return skip('none found');

      if (!isAnyIdAvailable(snap.localStorage.utiqPass?.connectId?.idGraph))
        return skip('None available');

      function parseEntry(idGraph: IdGraphType) {
        const { mtid, atid: idGraphAdtechpass, attrid, category } = idGraph;

        const atid = getAdtechpassValueProxy(idGraphAdtechpass);
        const domain = snap.localStorage.utiqPass?.connectId?.domain;
        const ttl = snap.localStorage.utiqPass?.connectId?.ttl;

        return {
          entry: {
            mtid,
            atid,
            attrid,
            category,
            domain,
            ttl,
          },
        };
      }

      const entries =
        snap.localStorage.utiqPass?.connectId.idGraph.map(parseEntry);

      const head = entries[0];

      if (!head) return skip('none found');

      return pass({
        ...head.entry,
        entries: entries.map((x) => x.entry),
      });
    },

    (payload, { ctx, name }) => {
      ctx.journal[name] = ctx.window.localStorage.getItem('utiqPass');
      callEventListeners('onIdsAvailable', 'Utiq', payload);
      callEventListeners('onFlowCompleted', 'Utiq', {});
      triggerUtiqSecureSignalProvider(payload.atid);
    },
  ),

  makeEffect(
    'onConsentUpdateFinishedInit',

    (_, { ctx, pass, skip }) => {
      if (ctx.journal.onConsentUpdateFinished === undefined)
        return pass('init');

      return skip('');
    },

    (_, { ctx, snap }) => {
      ctx.journal.onConsentUpdateFinished = snap.cookies.utiq_consent_status;
    },
  ),

  makeEffect(
    'onConsentUpdateFinished',

    (_, { ctx, snap, pass, skip }) => {
      if (ctx.journal.onConsentUpdateFinished === undefined)
        return skip('has not been initialized');

      if (snap.cookies.utiq_consent_status === null) return skip('cookie null');

      if (
        snap.cookies.utiq_consent_status === ctx.journal.onConsentUpdateFinished
      )
        return skip('unchanged');

      if (
        (snap.cookies.utiq_consent_status === 'true' ||
          snap.cookies.utiq_consent_status === 'false') &&
        snap.cookies.utiq_consent_status !== ctx.journal.onConsentUpdateFinished
      )
        return pass({
          isConsentGranted: snap.cookies.utiq_consent_status === 'true',
        });

      return skip('');
    },

    (payload, { ctx, snap }) => {
      ctx.journal.onConsentUpdateFinished = snap.cookies.utiq_consent_status;
      callEventListeners('onConsentUpdateFinished', 'Utiq', payload);
    },
  ),
];
